import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Header from './Header.js';
import Footer from './Footer.js';
import NewsletterSubscription from './NewsletterSubscription.js';
import Picture from './components/Picture.jsx';
import { loadGoogleMapsScript } from './utils/googleMapsLoader.js';
import { formatDistance, haversineDistance } from './utils/geo.js';
import {
  AdjustmentsHorizontalIcon,
  ArrowTopRightOnSquareIcon,
  MapPinIcon,
  PhoneIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { ChatBubbleLeftIcon } from '@heroicons/react/24/solid';

const DEFAULT_CENTER = { lat: -14.235004, lng: -51.92528 }; // Centro do Brasil
const RADIUS_OPTIONS = [
  { value: 25, label: '25 km' },
  { value: 50, label: '50 km' },
  { value: 100, label: '100 km' },
  { value: 200, label: '200 km' },
  { value: 0, label: 'Todo o Brasil' },
];

const CATEGORY_CONFIG = {
  master_distributor: {
    label: 'Master Distributor',
    badgeLabel: 'Master Distributor',
    color: '#f59e0b',
  },
  accredited_detailer: {
    label: 'Aplicador Credenciado',
    badgeLabel: 'Aplicador Credenciado',
    color: '#ff0000',
  },
  mobile_detailer: {
    label: 'Aplicador Credenciado Mobile',
    badgeLabel: 'Aplicador Credenciado Mobile',
    color: '#6b7280',
  },
  reseller: {
    label: 'Revendedor Gtechniq',
    badgeLabel: 'Revendedor',
    color: '#2563eb',
  },
};

// Mobile detailers should appear last in lists
const CATEGORY_ORDER = ['master_distributor', 'accredited_detailer', 'reseller', 'mobile_detailer'];
const ACCREDITED_DATA_URL = '/api/accredited.json';
const SELECTED_PARTNER_ZOOM = 14;
const PAN_ANIMATION_THRESHOLD_KM = 15;
const SMOOTH_PAN_MIN_DURATION_MS = 300;
const SMOOTH_PAN_MAX_DURATION_MS = 1500;

const normalizeText = (value) => (value || '').normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();
const sanitizePhoneLink = (value) => (value || '').replace(/[^+\d]/g, '');
const ensureCategories = (value) =>
  Array.isArray(value) && value.length ? value : ['accredited_detailer'];

const getPrimaryCategory = (categories) => {
  const normalized = ensureCategories(categories);
  for (const key of CATEGORY_ORDER) {
    if (normalized.includes(key)) {
      return key;
    }
  }
  return 'accredited_detailer';
};

const createMarkerIcon = (primaryCategory, categories) => {
  const resolvedCategories = ensureCategories(categories);
  const palette = resolvedCategories
    .map((category) => CATEGORY_CONFIG[category]?.color)
    .filter(Boolean);

  const baseColor = CATEGORY_CONFIG[primaryCategory]?.color || '#ff0000';
  const uniqueId = Math.random().toString(36).slice(2, 10);

  const colors = palette.length ? palette.slice(0, 2) : [baseColor];
  const hasSplit = colors.length > 1;
  const gradientId = `grad-${uniqueId}`;

  const gradientDefinition = hasSplit
    ? `<linearGradient id="${gradientId}" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stop-color="${colors[0]}" />
        <stop offset="50%" stop-color="${colors[0]}" />
        <stop offset="50%" stop-color="${colors[1]}" />
        <stop offset="100%" stop-color="${colors[1]}" />
      </linearGradient>`
    : `<linearGradient id="${gradientId}" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stop-color="${baseColor}" stop-opacity="1" />
        <stop offset="100%" stop-color="${baseColor}" stop-opacity="0.85" />
      </linearGradient>`;

  const svg = `<?xml version="1.0" encoding="UTF-8"?>
    <svg xmlns="http://www.w3.org/2000/svg" width="48" height="64" viewBox="0 0 48 64">
      <defs>
        ${gradientDefinition}
        <filter id="shadow-${uniqueId}" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="2" stdDeviation="2" flood-color="rgba(15,23,42,0.25)" />
        </filter>
      </defs>
      <g filter="url(#shadow-${uniqueId})">
        <path d="M24 2C14.059 2 6 10.059 6 20c0 14.238 18 32 18 32s18-17.762 18-32C42 10.059 33.941 2 24 2z" fill="url(#${gradientId})" stroke="white" stroke-width="2" />
        <circle cx="24" cy="20" r="8" fill="white" fill-opacity="0.9" />
        <circle cx="24" cy="20" r="5" fill="${baseColor}" />
      </g>
    </svg>`;

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

const AccreditedFinderPage = () => {
  const [partners, setPartners] = useState([]);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [dataError, setDataError] = useState('');

  const [mapsReady, setMapsReady] = useState(false);
  const [mapsError, setMapsError] = useState('');
  const [supportsWebp, setSupportsWebp] = useState(true);
  const [mapTilesReady, setMapTilesReady] = useState(false);
  const [mapFallbackActive, setMapFallbackActive] = useState(false);

  const [selectedState, setSelectedState] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [radiusKm, setRadiusKm] = useState(50);

  const [mapCenter, setMapCenter] = useState(DEFAULT_CENTER);
  const [centerSource, setCenterSource] = useState('default');
  const [centerLabel, setCenterLabel] = useState('Brasil');

  const [selectedPartner, setSelectedPartner] = useState(null);
  const [selectedPartnerFocus, setSelectedPartnerFocus] = useState({ id: null, index: 0 });
  const [statusMessage, setStatusMessage] = useState('');

  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const partnerRefs = useRef({});
  const autocompleteRef = useRef(null);
  const autocompleteInstanceRef = useRef(null);
  const mapPanAnimationRef = useRef(null);
  const mapFallbackTimerRef = useRef(null);

  const cancelActivePanAnimation = useCallback(() => {
    if (mapPanAnimationRef.current != null) {
      cancelAnimationFrame(mapPanAnimationRef.current);
      mapPanAnimationRef.current = null;
    }
  }, []);

  const smoothPanTo = useCallback(
    (mapInstance, fromPoint, toPoint, distanceKm) => {
      if (!mapInstance || !fromPoint || !toPoint) return;

      cancelActivePanAnimation();

      const duration = (() => {
        if (distanceKm == null) return 600;
        const scaled = distanceKm * 20 + 300;
        return Math.max(
          SMOOTH_PAN_MIN_DURATION_MS,
          Math.min(scaled, SMOOTH_PAN_MAX_DURATION_MS)
        );
      })();

      const startLat = fromPoint.lat;
      const startLng = fromPoint.lng;
      const deltaLat = toPoint.lat - startLat;
      const deltaLng = toPoint.lng - startLng;
      const startTime = performance.now();

      const step = (now) => {
        const elapsed = now - startTime;
        const progress = duration > 0 ? Math.min(elapsed / duration, 1) : 1;

        mapInstance.setCenter({
          lat: startLat + deltaLat * progress,
          lng: startLng + deltaLng * progress,
        });

        if (progress < 1) {
          mapPanAnimationRef.current = requestAnimationFrame(step);
        } else {
          mapPanAnimationRef.current = null;
        }
      };

      mapPanAnimationRef.current = requestAnimationFrame(step);
    },
    [cancelActivePanAnimation]
  );

  const [categoryFilters, setCategoryFilters] = useState(() =>
    CATEGORY_ORDER.reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {})
  );

  const activeCategories = useMemo(
    () => CATEGORY_ORDER.filter((key) => categoryFilters[key]),
    [categoryFilters]
  );

  useEffect(() => {
    const loadData = async () => {
      try {
        const response = await fetch(ACCREDITED_DATA_URL, { cache: 'no-store' });
        if (!response.ok) {
          throw new Error(`Falha ao carregar dados (${response.status})`);
        }
        const payload = await response.json();
        if (!Array.isArray(payload)) {
          throw new Error('Formato inválido de dados.');
        }
        setPartners(payload);
        setDataError('');
      } catch (error) {
        console.error('Erro ao carregar accredited.json', error);
        setDataError('Não foi possível carregar a lista de parceiros. Tente novamente mais tarde.');
      } finally {
        setIsLoadingData(false);
      }
    };

    loadData();
  }, []);

  useEffect(() => () => {
    cancelActivePanAnimation();
    if (mapFallbackTimerRef.current) {
      clearTimeout(mapFallbackTimerRef.current);
      mapFallbackTimerRef.current = null;
    }
  }, [cancelActivePanAnimation]);

  useEffect(() => {
    let cancelled = false;
    const image = new Image();
    image.onload = () => {
      if (!cancelled) {
        setSupportsWebp(image.width > 0 && image.height > 0);
      }
    };
    image.onerror = () => {
      if (!cancelled) {
        setSupportsWebp(false);
      }
    };
    image.src = 'data:image/webp;base64,UklGRiIAAABXRUJQVlA4IBgAAAAwAQCdASoEAAQAAVAfCWkAQUAAkA==';

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_KEY;
    if (!apiKey) {
      setMapsError('Configure a variável REACT_APP_GOOGLE_MAPS_KEY para exibir o mapa.');
      return;
    }

    loadGoogleMapsScript(apiKey)
      .then(() => {
        setMapsReady(true);
        setMapsError('');
      })
      .catch((error) => {
        console.error('Erro ao carregar Google Maps', error);
        setMapsError('Não foi possível carregar o Google Maps.');
      });
  }, []);

  useEffect(() => {
    if (!mapsReady || !mapContainerRef.current) return;

    if (!mapInstanceRef.current) {
      mapInstanceRef.current = new window.google.maps.Map(mapContainerRef.current, {
        center: DEFAULT_CENTER,
        zoom: 4,
        mapTypeControl: false,
        streetViewControl: false,
        zoomControl: false,
        fullscreenControl: true,
      });
      setMapTilesReady(false);
      setMapFallbackActive(false);
    }

    const listener = mapInstanceRef.current.addListener('tilesloaded', () => {
      setMapTilesReady(true);
      setMapFallbackActive(false);
    });

    return () => {
      if (listener) {
        listener.remove();
      }
    };
  }, [mapsReady]);

  useEffect(() => {
    if (!mapsReady || mapTilesReady || mapsError) {
      if (mapFallbackTimerRef.current) {
        clearTimeout(mapFallbackTimerRef.current);
        mapFallbackTimerRef.current = null;
      }
      if (mapTilesReady) {
        setMapFallbackActive(false);
      }
      return;
    }

    if (!mapFallbackActive && !mapFallbackTimerRef.current) {
      const delay = supportsWebp ? 10000 : 6000;
      mapFallbackTimerRef.current = setTimeout(() => {
        setMapFallbackActive(true);
        mapFallbackTimerRef.current = null;
      }, delay);
    }

    return () => {
      if (mapFallbackTimerRef.current) {
        clearTimeout(mapFallbackTimerRef.current);
        mapFallbackTimerRef.current = null;
      }
    };
  }, [mapsReady, mapTilesReady, supportsWebp, mapFallbackActive, mapsError]);

  useEffect(() => {
    if (!mapsReady || !mapInstanceRef.current) return;
    if (!autocompleteRef.current) return;
    if (autocompleteInstanceRef.current) return;

    const autocomplete = new window.google.maps.places.Autocomplete(autocompleteRef.current, {
      fields: ['geometry', 'formatted_address', 'name'],
      componentRestrictions: { country: ['br'] },
    });

    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      const location = place?.geometry?.location;
      if (!location) return;

      const coords = { lat: location.lat(), lng: location.lng() };
      setMapCenter(coords);
      setCenterSource('search');
      setCenterLabel(place.name || place.formatted_address || 'Local selecionado');
      setSelectedState('');
      setSelectedCity('');
      setSearchTerm('');
    });

    autocompleteInstanceRef.current = autocomplete;
  }, [mapsReady]);

  const states = useMemo(() => {
    const values = new Set((Array.isArray(partners) ? partners : []).map((item) => item?.state || ''));
    return Array.from(values)
      .filter(Boolean)
      .sort((a, b) => String(a).localeCompare(String(b)));
  }, [partners]);

  const citiesForState = useMemo(() => {
    if (!selectedState) return [];
    const list = Array.isArray(partners) ? partners : [];
    const values = new Set(list.filter((item) => item?.state === selectedState).map((item) => item?.city || ''));
    return Array.from(values)
      .filter(Boolean)
      .sort((a, b) => String(a).localeCompare(String(b)));
  }, [partners, selectedState]);

  const shouldApplyRadius = centerSource !== 'default' && radiusKm > 0;

  const MOBILE_CITY_MARKERS = {
    'São Paulo|SP': { lat: -23.5557714, lng: -46.6395571 },
    'Rio de Janeiro|RJ': { lat: -22.9068467, lng: -43.1728965 },
    'Brasília|DF': { lat: -15.793889, lng: -47.882778 },
  };

  const enrichedPartners = useMemo(() => {
    const normalizedTerm = normalizeText(searchTerm);

    const items = partners
      .map((item) => {
        const categories = ensureCategories(item.categories);
        const primaryCategory = getPrimaryCategory(categories);
        const isMobile = categories.includes('mobile_detailer');
        const cityKey = `${item.city}|${item.state}`;
        const markerCoords = isMobile && MOBILE_CITY_MARKERS[cityKey]
          ? MOBILE_CITY_MARKERS[cityKey]
          : { lat: item.lat, lng: item.lng };
        const markerLocation = {
          lat: markerCoords.lat,
          lng: markerCoords.lng,
          city: item.city,
          state: item.state,
        };
        const distanceKm = haversineDistance(mapCenter, markerLocation);

        return {
          ...item,
          categories,
          primaryCategory,
          isMobile,
          markerLat: markerLocation.lat,
          markerLng: markerLocation.lng,
          markerLocations: [markerLocation],
          distanceKm,
        };
      })
      .filter((item) => {
        if (!item.categories.some((category) => activeCategories.includes(category))) return false;
        if (selectedState && item.state !== selectedState) return false;
        if (selectedCity && item.city !== selectedCity) return false;

        if (normalizedTerm) {
          const haystack = normalizeText(
            `${item.name} ${item.city} ${item.state} ${item.address} ${item.postal_code}`
          );
          if (!haystack.includes(normalizedTerm)) return false;
        }

        if (shouldApplyRadius && item.distanceKm != null && item.distanceKm > radiusKm) {
          return false;
        }

        return true;
      })
      // Deduplicate mobile detailers with same name, aggregating service areas
      .reduce((acc, cur) => {
        if (!cur.isMobile) {
          acc.push(cur);
          return acc;
        }

        const existing = acc.find((entry) => entry.isMobile && normalizeText(entry.name) === normalizeText(cur.name));
        const areaLabel = `${cur.city} - ${cur.state}`;
        const markerLocation = cur.markerLocations?.[0];

        if (existing) {
          existing.serviceAreaSet = existing.serviceAreaSet || new Set([`${existing.city} - ${existing.state}`]);
          existing.serviceAreaSet.add(areaLabel);

          if (markerLocation) {
            existing.markerLocations = existing.markerLocations || [];
            const alreadyHas = existing.markerLocations.some(
              (pos) => Math.abs(pos.lat - markerLocation.lat) < 1e-6 && Math.abs(pos.lng - markerLocation.lng) < 1e-6
            );
            if (!alreadyHas) {
              existing.markerLocations.push(markerLocation);
            }
          }

          if (cur.distanceKm != null) {
            existing.distanceKm = existing.distanceKm != null
              ? Math.min(existing.distanceKm, cur.distanceKm)
              : cur.distanceKm;
          }
        } else {
          const copy = { ...cur };
          copy.serviceAreaSet = new Set([areaLabel]);
          copy.markerLocations = markerLocation ? [markerLocation] : [];
          copy.distanceKm = cur.distanceKm;
          acc.push(copy);
        }

        return acc;
      }, [])
      .map((item) => {
        let markerLocations = item.markerLocations && item.markerLocations.length
          ? item.markerLocations
          : [{ lat: item.markerLat ?? item.lat, lng: item.markerLng ?? item.lng, city: item.city, state: item.state }];

        markerLocations = markerLocations.map((pos) => ({
          lat: pos.lat,
          lng: pos.lng,
          city: pos.city ?? item.city,
          state: pos.state ?? item.state,
        }));

        let serviceAreas;
        if (item.isMobile) {
          if (item.serviceAreaSet instanceof Set) {
            serviceAreas = Array.from(item.serviceAreaSet);
          } else if (Array.isArray(item.serviceAreas) && item.serviceAreas.length) {
            serviceAreas = item.serviceAreas;
          }

          if (!serviceAreas || !serviceAreas.length) {
            const rawAreas = String(item.address || '')
              .split(/\s*;\s*/)
              .map((value) => value.trim())
              .filter(Boolean);
            if (rawAreas.length) {
              serviceAreas = rawAreas;
            }
          }

          if (!serviceAreas || !serviceAreas.length) {
            serviceAreas = [`${item.city} - ${item.state}`];
          }
        }

        const distanceCandidates = markerLocations
          .map((pos) => haversineDistance(mapCenter, pos))
          .filter((value) => value != null);

        const distanceKm = distanceCandidates.length
          ? Math.min(...distanceCandidates)
          : item.distanceKm ?? null;

        const displayAddress = item.isMobile
          ? serviceAreas && serviceAreas.length ? serviceAreas.join(' • ') : ''
          : item.address;

        const { serviceAreaSet, ...rest } = item;

        return {
          ...rest,
          markerLocations,
          serviceAreas,
          displayAddress,
          distanceKm,
        };
      })
      .sort((a, b) => {
        if (shouldApplyRadius) {
          return (a.distanceKm || 0) - (b.distanceKm || 0);
        }
        if (a.primaryCategory !== b.primaryCategory) {
          return CATEGORY_ORDER.indexOf(a.primaryCategory) - CATEGORY_ORDER.indexOf(b.primaryCategory);
        }
        return a.name.localeCompare(b.name);
      });

    return items;
  }, [partners, activeCategories, selectedState, selectedCity, searchTerm, mapCenter, shouldApplyRadius, radiusKm]);

  useEffect(() => {
    if (!mapsReady || !mapInstanceRef.current) return;

    cancelActivePanAnimation();

      markersRef.current.forEach((marker) => marker.setMap(null));
    markersRef.current = [];

    if (!enrichedPartners.length) {
      mapInstanceRef.current.setCenter(mapCenter);
      mapInstanceRef.current.setZoom(centerSource === 'default' ? 4 : 9);
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();
    const hasSelectedPartner = Boolean(selectedPartner);

    enrichedPartners.forEach((item) => {
      const markerIcon = createMarkerIcon(item.primaryCategory, item.categories);
      const markerPositions = item.markerLocations && item.markerLocations.length
        ? item.markerLocations
        : [{ lat: item.markerLat ?? item.lat, lng: item.markerLng ?? item.lng, city: item.city, state: item.state }];

      markerPositions.forEach((position, index) => {
        const marker = new window.google.maps.Marker({
          map: mapInstanceRef.current,
          position: { lat: position.lat, lng: position.lng },
          title: markerPositions.length > 1 && position.city
            ? `${item.name} (${position.city}${position.state ? ` - ${position.state}` : ''})`
            : item.name,
          icon: {
            url: markerIcon,
            scaledSize: new window.google.maps.Size(36, 48),
            anchor: new window.google.maps.Point(18, 46),
          },
          zIndex: item.primaryCategory === 'master_distributor' ? 1200 : undefined,
        });

        marker.__detailerId = item.id;
        marker.__detailerPositionIndex = index;

        marker.addListener('click', () => {
          setSelectedPartner(item);
          setSelectedPartnerFocus({ id: item.id, index });
        });

        markersRef.current.push(marker);
        bounds.extend(marker.getPosition());
      });
    });

    if (centerSource !== 'default') {
      bounds.extend(new window.google.maps.LatLng(mapCenter.lat, mapCenter.lng));
    }

    if (!bounds.isEmpty() && !hasSelectedPartner) {
      mapInstanceRef.current.fitBounds(bounds, 80);
      if (enrichedPartners.length === 1) {
        mapInstanceRef.current.setZoom(SELECTED_PARTNER_ZOOM);
      }
    }
  }, [enrichedPartners, mapsReady, mapCenter, centerSource, selectedPartner, cancelActivePanAnimation]);

  useEffect(() => {
    if (!mapsReady || !mapInstanceRef.current) return;

    if (!selectedPartner) {
      if (centerSource === 'default') {
        mapInstanceRef.current.setCenter(DEFAULT_CENTER);
        mapInstanceRef.current.setZoom(4);
      }
      return;
    }

    const stillVisible = enrichedPartners.some((item) => item.id === selectedPartner.id);
    if (!stillVisible) {
      setSelectedPartner(null);
      setSelectedPartnerFocus({ id: null, index: 0 });
      return;
    }

    const partnerMarkers = markersRef.current.filter((marker) => marker.__detailerId === selectedPartner.id);
    if (partnerMarkers.length) {
      const focusIndex = selectedPartnerFocus.id === selectedPartner.id
        ? Math.min(selectedPartnerFocus.index, partnerMarkers.length - 1)
        : 0;
      const targetMarker = partnerMarkers[focusIndex];
      const targetPosition = targetMarker.getPosition();
      const currentCenter = mapInstanceRef.current.getCenter();

      if (targetPosition) {
        const targetPoint = {
          lat: targetPosition.lat(),
          lng: targetPosition.lng(),
        };

        const currentPoint = currentCenter
          ? { lat: currentCenter.lat(), lng: currentCenter.lng() }
          : null;
        const distanceKm = currentPoint ? haversineDistance(currentPoint, targetPoint) : null;
        const shouldAnimatePan = currentPoint && distanceKm != null
          ? distanceKm <= PAN_ANIMATION_THRESHOLD_KM
          : true;

        if (shouldAnimatePan && currentPoint) {
          smoothPanTo(mapInstanceRef.current, currentPoint, targetPoint, distanceKm);
        } else {
          cancelActivePanAnimation();
          mapInstanceRef.current.setCenter(targetPoint);
        }
      }
      if (mapInstanceRef.current.getZoom() < SELECTED_PARTNER_ZOOM) {
        mapInstanceRef.current.setZoom(SELECTED_PARTNER_ZOOM);
      }
    }
  }, [selectedPartner, selectedPartnerFocus, enrichedPartners, mapsReady, centerSource, smoothPanTo, cancelActivePanAnimation]);

  useEffect(() => {
    if (!selectedPartner) return;
    const node = partnerRefs.current[selectedPartner.id];
    if (node && node.scrollIntoView) {
      node.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [selectedPartner]);

  const handleUseMyLocation = () => {
    if (!navigator.geolocation) {
      setStatusMessage('Seu navegador não permite geolocalização.');
      return;
    }

    setStatusMessage('Localizando...');
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const coords = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        setMapCenter(coords);
        setCenterSource('geolocation');
        setCenterLabel('Sua localização');
        setSelectedState('');
        setSelectedCity('');
        setSearchTerm('');
        if (autocompleteRef.current) {
          autocompleteRef.current.value = '';
        }
        setStatusMessage('');
      },
      () => {
        setStatusMessage('Não foi possível obter sua localização.');
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleResetFilters = () => {
    setSelectedState('');
    setSelectedCity('');
    setSearchTerm('');
    setRadiusKm(50);
    setMapCenter(DEFAULT_CENTER);
    setCenterSource('default');
    setCenterLabel('Brasil');
    setSelectedPartner(null);
    setSelectedPartnerFocus({ id: null, index: 0 });
    if (autocompleteRef.current) {
      autocompleteRef.current.value = '';
    }
    setCategoryFilters(
      CATEGORY_ORDER.reduce((acc, key) => {
        acc[key] = true;
        return acc;
      }, {})
    );
  };

  const toggleCategory = (categoryKey) => {
    setCategoryFilters((prev) => {
      const next = { ...prev, [categoryKey]: !prev[categoryKey] };
      if (!Object.values(next).includes(true)) {
        return prev;
      }
      return next;
    });
  };

  const infoSummary = useMemo(() => {
    if (!enrichedPartners.length) return 'Nenhum parceiro encontrado para os filtros selecionados.';

    const base = `${enrichedPartners.length} parceiro${enrichedPartners.length > 1 ? 's' : ''} encontrado${
      enrichedPartners.length > 1 ? 's' : ''
    }`;

    if (centerSource === 'default') return base;
    const distanceNote = ` baseados em ${centerLabel}`;
    return `${base}${distanceNote}`;
  }, [enrichedPartners, centerSource, centerLabel]);

const CategoryBadges = ({ categories }) => {
  const normalized = useMemo(() => ensureCategories(categories), [categories]);
  const isDual = normalized.includes('accredited_detailer') && normalized.includes('reseller');
  const containerRef = useRef(null);
  const [wrapped, setWrapped] = useState(false);

  useEffect(() => {
    const checkWrap = () => {
      const container = containerRef.current;
      if (!container) return;
      const children = Array.from(container.children);
      if (children.length <= 1) {
        setWrapped(false);
        return;
      }
      const firstTop = children[0].offsetTop;
      const anyWrapped = children.some((child) => child.offsetTop - firstTop > 1);
      setWrapped(anyWrapped);
    };

    checkWrap();

    let resizeObserver;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(checkWrap);
      if (containerRef.current) resizeObserver.observe(containerRef.current);
    } else {
      window.addEventListener('resize', checkWrap);
    }

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      } else {
        window.removeEventListener('resize', checkWrap);
      }
    };
  }, [normalized]);

  const shouldOverlap = !wrapped && isDual;

  return (
    <div
      ref={containerRef}
      className={`flex gap-x-0 gap-y-1 ${shouldOverlap ? 'flex-nowrap' : 'flex-wrap'}`}
    >
      {normalized.map((category) => {
        const config = CATEGORY_CONFIG[category];
        if (!config) return null;
        const isReseller = category === 'reseller';

        const paddingClasses = shouldOverlap && isReseller ? 'pl-6 pr-6' : 'pl-4 pr-5';
        const baseClasses = `inline-flex items-center ${paddingClasses} py-1.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-white transition-colors`;

        const style = {
          background: `linear-gradient(135deg, ${config.color} 0%, ${config.color}e0 70%, ${config.color} 100%)`,
          clipPath: 'polygon(0% 0%, 100% 0%, calc(100% - 16px) 100%, 0% 100%)',
        };

        if (shouldOverlap) {
          style.clipPath = isReseller
            ? 'polygon(16px 0%, 100% 0%, calc(100% - 16px) 100%, 0% 100%)'
            : 'polygon(0% 0%, 100% 0%, calc(100% - 16px) 100%, 0% 100%)';
        } else if (isDual && wrapped) {
          style.clipPath = 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)';
        }

        const extraClasses = [];
        if (shouldOverlap && isDual) {
          extraClasses.push('relative');
          if (isReseller) {
            extraClasses.push('z-0');
            style.marginLeft = '-10px';
          } else {
            extraClasses.push('z-10');
          }
        }

        return (
          <span key={`${category}-${config.badgeLabel}`} className={`${baseClasses} ${extraClasses.join(' ')}`} style={style}>
            {config.badgeLabel}
          </span>
        );
      })}
    </div>
  );
};

  const renderPartnerCard = (item) => {
    const whatsappLink = item.whatsapp
      ? `https://wa.me/${sanitizePhoneLink(item.whatsapp)}?text=${encodeURIComponent(
          'Olá! Gostaria de saber mais sobre a aplicação Gtechniq.'
        )}`
      : null;

    const phoneLink = item.phone ? `tel:${sanitizePhoneLink(item.phone)}` : null;
    const primaryLocation = item.markerLocations && item.markerLocations.length
      ? item.markerLocations[0]
      : { lat: item.markerLat ?? item.lat, lng: item.markerLng ?? item.lng };
    const destLat = primaryLocation.lat;
    const destLng = primaryLocation.lng;
    const directionsLink = `https://www.google.com/maps/dir/?api=1&destination=${destLat},${destLng}`;

    const isSelected = selectedPartner && selectedPartner.id === item.id;

    return (
      <article
        key={item.id}
        className={`border border-gray-200 p-5 bg-white shadow-sm transition-all duration-200 cursor-pointer ${
          isSelected
            ? 'ring-2 ring-[#FF0000] shadow-lg translate-y-[-2px]'
            : 'hover:shadow-lg hover:-translate-y-1'
        }`}
        ref={(node) => {
          if (node) {
            partnerRefs.current[item.id] = node;
          } else {
            delete partnerRefs.current[item.id];
          }
        }}
        onClick={() => {
          setSelectedPartner((current) => {
            const next = current && current.id === item.id ? null : item;
            setSelectedPartnerFocus(next ? { id: item.id, index: 0 } : { id: null, index: 0 });
            return next;
          });
        }}
      >
        <div className="flex flex-col xl:flex-row xl:items-start gap-4">
          {isSelected && (
            <picture className="xl:hidden">
              <source srcSet={item.photo} type="image/webp" />
              <img
                src={item.photo?.replace(/\.webp$/i, '.jpg')}
                alt={item.name}
                className="w-full h-40 object-cover rounded-md bg-gray-100"
                loading="lazy"
              />
            </picture>
          )}
          {isSelected && (
            <div className="hidden xl:flex xl:flex-col xl:items-start">
              <picture>
                <source srcSet={item.photo} type="image/webp" />
                <img
                  src={item.photo?.replace(/\.webp$/i, '.jpg')}
                  alt={item.name}
                  className="w-20 h-20 object-cover rounded-md bg-gray-100"
                  loading="lazy"
                />
              </picture>
            </div>
          )}
          <div className="flex-1 space-y-2">
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-lg font-semibold text-gray-900">{item.name}</h3>
              {item.distanceKm != null && shouldApplyRadius && (
                <span className="text-sm font-medium text-gray-500">{formatDistance(item.distanceKm)}</span>
              )}
            </div>
            <CategoryBadges categories={item.categories} />
            <p className="text-sm text-gray-600 flex items-center gap-1">
              <MapPinIcon className="h-4 w-4 text-[#FF0000]" />
              {item.city}, {item.state}
            </p>
            {item.isMobile ? (
              item.displayAddress && (
                <p className="text-sm text-gray-500">Atende: {item.displayAddress}</p>
              )
            ) : (
              item.displayAddress && <p className="text-sm text-gray-500">{item.displayAddress}</p>
            )}
            <div className="flex flex-wrap gap-2 pt-2">
              {whatsappLink && (
                <a
                  href={whatsappLink}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 rounded-full bg-[#25D366]/10 text-[#128C7E] px-3 py-1 text-xs font-semibold hover:bg-[#25D366]/20"
                  onClick={(event) => event.stopPropagation()}
                >
                  <ChatBubbleLeftIcon className="h-3.5 w-3.5" /> WhatsApp
                </a>
              )}
              {phoneLink && (
                <a
                  href={phoneLink}
                  className="inline-flex items-center gap-1 rounded-full bg-gray-100 text-gray-700 px-3 py-1 text-xs font-semibold hover:bg-gray-200"
                  onClick={(event) => event.stopPropagation()}
                >
                  <PhoneIcon className="h-3.5 w-3.5" /> Ligar
                </a>
              )}
              {!item.isMobile && (
                <a
                  href={directionsLink}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 rounded-full bg-gray-100 text-gray-700 px-3 py-1 text-xs font-semibold hover:bg-gray-200"
                  onClick={(event) => event.stopPropagation()}
                >
                  <MapPinIcon className="h-3.5 w-3.5" /> Como chegar
                </a>
              )}
              {item.website && (
                <a
                  href={item.website}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 rounded-full bg-gray-100 text-gray-700 px-3 py-1 text-xs font-semibold hover:bg-gray-200"
                  onClick={(event) => event.stopPropagation()}
                >
                  <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" /> Site
                </a>
              )}
              {item.instagram && (
                <a
                  href={item.instagram}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 rounded-full bg-gray-100 text-gray-700 px-3 py-1 text-xs font-semibold hover:bg-gray-200"
                  onClick={(event) => event.stopPropagation()}
                >
                  <SparklesIcon className="h-3.5 w-3.5" /> Instagram
                </a>
              )}
            </div>
          </div>
        </div>
      </article>
    );
  };

  return (
    <div className="font-bai-jamjuree text-gray-800 bg-white min-h-screen">
      <Header />
      <main>
        <section className="relative h-[70vh] flex items-end text-white overflow-hidden pb-32">
          <div className="absolute inset-0" aria-hidden="true">
            <Picture
              src="/images/background/paint-protection-image-min-1-scaled.webp"
              fallbackExt="png"
              alt=""
              pictureClassName="block h-full w-full"
              className="h-full w-full object-cover"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-black/50" />
          <div className="container mx-auto px-4 md:px-10 relative z-10 w-full">
            <div className="max-w-3xl py-24 md:py-32">
              <span className="inline-block uppercase tracking-[0.35em] text-xs md:text-sm text-[#FF0000] font-semibold mb-4">
                Rede Credenciada
              </span>
              <h1 className="text-3xl md:text-5xl font-bold leading-tight mb-6">
                Encontre um profissional Gtechniq perto de você
              </h1>
              <p className="text-base md:text-lg text-gray-200 max-w-2xl">
                Busque por cidade, CEP ou use sua localização para descobrir rapidamente aplicadores credenciados, revendedores e o master distributor oficial Gtechniq Brasil.
              </p>
              <div className="mt-8">
                <a
                  href="https://gtechniq.com.br/seja-um-aplicador-oficial"
                  className="inline-flex items-center justify-center px-6 py-3 bg-[#FF0000] hover:bg-red-700 font-semibold tracking-wide uppercase text-white"
                >
                  Quero ser Aplicador Oficial
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="relative z-10 -mt-28 md:-mt-40 mb-24">
          <div className="container mx-auto px-4 md:px-10">
            <div className="bg-white border border-gray-200 shadow-2xl p-6 md:p-8 space-y-6">
              <div className="grid gap-4 md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)]">
                <div className="flex flex-col">
                  <label className="text-xs font-semibold uppercase tracking-wide text-gray-600 mb-2">Buscar por local ou nome</label>
                  <input
                    ref={autocompleteRef}
                    type="text"
                    placeholder="Digite cidade, bairro, CEP ou nome do estúdio"
                    onChange={(event) => setSearchTerm(event.target.value)}
                    className="border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF0000]"
                  />
                  <button
                    type="button"
                    onClick={handleUseMyLocation}
                    className="mt-2 inline-flex items-center justify-center gap-2 text-xs font-semibold uppercase tracking-wide text-[#FF0000]"
                  >
                    <MapPinIcon className="h-4 w-4" /> Usar minha localização
                  </button>
                </div>
                <div className="flex flex-col">
                  <label className="text-xs font-semibold uppercase tracking-wide text-gray-600 mb-2">Estado</label>
                  <select
                    value={selectedState}
                    onChange={(event) => {
                      setSelectedState(event.target.value);
                      setSelectedCity('');
                    }}
                    className="border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF0000]"
                  >
                    <option value="">Todos</option>
                    {states.map((state) => (
                      <option key={state} value={state}>
                        {state}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-col">
                  <label className="text-xs font-semibold uppercase tracking-wide text-gray-600 mb-2">Cidade</label>
                  <select
                    value={selectedCity}
                    onChange={(event) => setSelectedCity(event.target.value)}
                    disabled={!selectedState}
                    className="border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF0000] disabled:bg-gray-100"
                  >
                    <option value="">Todas</option>
                    {citiesForState.map((city) => (
                      <option key={city} value={city}>
                        {city}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-col">
                  <label className="text-xs font-semibold uppercase tracking-wide text-gray-600 mb-2">Raio</label>
                  <select
                    value={radiusKm}
                    onChange={(event) => setRadiusKm(Number(event.target.value))}
                    className="border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF0000]"
                  >
                    {RADIUS_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between gap-4 flex-wrap text-sm text-gray-600">
                  <div className="flex items-center gap-2">
                    <AdjustmentsHorizontalIcon className="h-5 w-5 text-[#FF0000]" />
                    <span>{infoSummary}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    {centerSource !== 'default' && (
                      <span className="text-xs text-gray-500">Base: {centerLabel}</span>
                    )}
                    <button
                      type="button"
                      onClick={handleResetFilters}
                      className="text-xs font-semibold uppercase tracking-wide text-gray-500 hover:text-[#FF0000]"
                    >
                      Limpar filtros
                    </button>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-4 pt-3 border-t border-gray-200">
                  <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Tipos de parceiros:</span>
                  {CATEGORY_ORDER.map((category) => {
                    const config = CATEGORY_CONFIG[category];
                    const isActive = categoryFilters[category];
                    return (
                      <label
                        key={category}
                        className={`inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide cursor-pointer select-none ${
                          isActive ? 'text-gray-700' : 'text-gray-400'
                        }`}
                      >
                        <input
                          type="checkbox"
                          className="sr-only"
                          checked={isActive}
                          onChange={() => toggleCategory(category)}
                        />
                        <span className="flex items-center gap-2">
                          <span
                            className="h-3.5 w-3.5 rounded-full border border-white shadow"
                            style={{
                              backgroundColor: config.color,
                              opacity: isActive ? 1 : 0.35,
                            }}
                          />
                          {config.label}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {statusMessage && (
                <div className="text-xs text-[#FF0000]">{statusMessage}</div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] gap-6">
                <div className="relative h-[420px] lg:h-[520px] bg-gray-100 border border-gray-200 overflow-hidden">
                  <div ref={mapContainerRef} className="absolute inset-0" />
                  {mapFallbackActive ? (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-white/95 text-center px-6">
                      <p className="text-sm text-gray-700">
                        {supportsWebp
                          ? 'Não foi possível carregar o mapa interativo neste momento. Recarregue a página ou abra o Google Maps em uma nova aba para visualizar os parceiros.'
                          : 'Não foi possível carregar o mapa interativo com o suporte a WebP desativado. Abra o Google Maps em uma nova aba ou reative o suporte a imagens WebP para visualizar a localização dos parceiros.'}
                      </p>
                      <a
                        href="https://www.google.com/maps/search/Gtechniq+Brasil"
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center justify-center px-4 py-2 bg-[#FF0000] text-white text-xs font-semibold uppercase tracking-wide hover:bg-red-700 transition"
                      >
                        Abrir no Google Maps
                      </a>
                    </div>
                  ) : (
                    (!mapsReady || mapsError) && (
                      <div className={`absolute inset-0 flex items-center justify-center text-sm px-4 ${
                        mapsError ? 'text-[#FF0000]' : 'text-gray-500'
                      }`}>
                        {mapsError || 'Carregando mapa...'}
                      </div>
                    )
                  )}
                </div>
                <div className="space-y-4 max-h-[520px] overflow-y-auto px-2 py-2">
                  {isLoadingData && <p className="text-sm text-gray-500">Carregando parceiros...</p>}
                  {dataError && <p className="text-sm text-[#FF0000]">{dataError}</p>}
                  {!isLoadingData && !dataError && enrichedPartners.length === 0 && (
                    <p className="text-sm text-gray-500">
                      Nenhum parceiro corresponde aos filtros. Ajuste o raio, categorias ou selecione outro local.
                    </p>
                  )}
                  {enrichedPartners.map((item) => renderPartnerCard(item))}
                </div>
              </div>

            </div>
          </div>
        </section>
      </main>
      <NewsletterSubscription />
      <Footer />
    </div>
  );
};

export default AccreditedFinderPage;
